import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { DATABASE_UPLOADED_STATICS_URLS } from '../config/database_uploaded_statics_urls';
import {
  ENVIRONMENT_URL,
  FrontendSiteType,
  SUPPORT_EMAIL,
  WEBSITE_URL,
  XANO_WORKSPACE_CODE,
} from '../enum/environment_urls.enum';
import { EMAIL_CSS, EMAIL_SUBJECT, EMAIL_TEMPLATE_TYPE } from '../enum/time_automated_emails.enum';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class AutomatedEmailsService {
  constructor(private utilService: UtilService) {}

  /**
   * @description Generates the email body content based on the specified template type for a teacher-only user
   */
  async getEmailBody(user: any, templateType): Promise<{ to: string; subject: string; html: string }> {
    try {
      const { firstName, lastName, email } = user;
      const isStage = this.utilService.detectFrontendSiteType() === FrontendSiteType.STAGE;
      const loginUrl = `https://${isStage ? ENVIRONMENT_URL.Stage : ENVIRONMENT_URL.Portal}/auth/login/teachers-only`;
      const logoUrl = DATABASE_UPLOADED_STATICS_URLS.teacher_only_logo_image_url;
      const userName = `${firstName} ${lastName}`;
      const supportLink = `mailto:${SUPPORT_EMAIL}`;
      let htmlContent;
      switch (templateType) {
        case EMAIL_TEMPLATE_TYPE.WELCOME:
          htmlContent = this.buildWelcomeHtml(userName, loginUrl, logoUrl, supportLink);
          return {
            to: email,
            subject: EMAIL_SUBJECT.Welcome_Email,
            html: htmlContent,
          };

        case EMAIL_TEMPLATE_TYPE.FIRST_CLASSROOM_SETUP_MAIL:
          htmlContent = this.mailAfterFirstClassroomSetup(userName, loginUrl, logoUrl, supportLink);
          return {
            to: email,
            subject: EMAIL_SUBJECT.First_Classroom_Setup_Mail,
            html: htmlContent,
          };

        default:
          console.error(`Unknown email template type: ${templateType}`);
          return;
      }
    } catch (error) {
      console.log('🚀 ~ automated-emails.service.ts:33 ~ AutomatedEmailsService ~ getEmailBody ~ error:', error);
    }
  }

  /**
   * @description template when teacher-only user onboard successfully
   */

  private buildWelcomeHtml(userName: string, loginUrl: string, logoUrl: string, supportLink: string): string {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Welcome Email</title>
        <style>
         ${EMAIL_CSS}
        </style>
      </head>
      <body>
        <p class="info-text">Hi ${userName} 👋,</p>
        <p class="info-text">Welcome to Respond EDU! 🎉</p>

        <div>
          <img class="logo-img" src="${logoUrl}" alt="Respond EDU Logo" loading="lazy" />
        </div>

        <p class="info-text" style="margin-top: -7px;">
          To help you get started quickly, we've prepared a simple Quick Start Guide:
        </p>

        <p class="info-text">1. Log in to your account here: <a href="${loginUrl}">Click to login</a></p>
        <p class="info-text">2. Set up your classroom and create your pacing plan.</p>
        <p class="info-text">3. Explore how to create lessons, assignments, and quizzes effortlessly.</p>

        <p class="info-text">That's it — you're ready to go!</p>

        <p class="info-text" style="margin-top: 2rem;">
          Need help? Check our Support Center, watch the tutorials at the bottom of your screen on the platform, 
          or email <a href="${supportLink}">${SUPPORT_EMAIL}</a>. We're always here for you.
        </p>

        <p class="info-text" style="margin-top: 2rem;">Happy teaching,</p>
        <p class="info-text">The <a href="${WEBSITE_URL}">Respond EDU</a> Team</p>
      </body>
      </html>
    `;
  }

  /**
   * @description template to Congratulate teacher-only user on their first classroom and lesson plan setup
   */
  private mailAfterFirstClassroomSetup(
    userName: string,
    loginUrl: string,
    logoUrl: string,
    supportLink: string,
  ): string {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Ace Your Homework Assignments – Quick Tips 📖✏️</title>
        <style>
         ${EMAIL_CSS}
        </style>
      </head>
      <body>
        <p class="info-text">Hi ${userName} 👋,</p>

        <div>
          <img class="logo-img" src="${logoUrl}" alt="Respond EDU Logo" loading="lazy" />
        </div>

        <p class="info-text" style="margin-top: -7px;">
          Fantastic job creating your first classroom and pacing plan! You're all set for a smooth and organized teaching experience.
        </p>

         <p class="info-text" style="margin-top: -7px;">
          What's next? Feel free to explore creating engaging lessons, interactive assignments, and insightful quizzes for your students.
        </p>

        <p class="info-text" style="margin-top: -7px;">
          If you need help or inspiration, our Support Center is always ready, or watch the tutorials at the bottom of the screen on the platform. You can also reach out directly to <a href="${supportLink}">${SUPPORT_EMAIL}</a>.
        </p>

        <p class="info-text" style="margin-top: 2rem;">
          Keep up the great work!
        </p>

        <p class="info-text" style="margin-top: 2rem;">Warm regards, <a href="${WEBSITE_URL}">The Respond EDU</a> Team</p>
      </body>
      </html>
    `;
  }
}
